import React, { useEffect, useState } from "react";
import { ArrowUpRight, CheckCircle, PhoneCall, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";

export default function Contact() {
  const [challenges, setChallenges] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    challenge: "",
    service_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get("/challenges")
      .then((r) => setChallenges(r.data || []))
      .catch(() => setChallenges([]));
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.challenge || form.challenge.trim().length < 5) {
      toast.error("Please describe your challenge (min 5 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Your note reached HumInt. We'll reply personally.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        challenge: "",
        service_interest: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <main>
        <section className="relative pt-32 md:pt-40 pb-16 grid-bg overflow-hidden">
          <div className="absolute inset-0 beam pointer-events-none" />
          <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
            <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6 rise d-1">
              / Start Diagnosis
            </div>
            <h1
              data-testid="contact-headline"
              className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-white max-w-4xl rise d-2"
            >
              Tell us what&apos;s
              <span className="block text-cy">keeping you up.</span>
            </h1>
            <p className="mt-8 max-w-xl text-zinc-400 text-base md:text-lg rise d-3">
              A HumInt partner reads every inquiry personally. No bots, no auto-responders.
            </p>
          </div>
        </section>

        <section className="bg-[#070709] border-y border-ink-600">
          <div id="direct-lines" className="scroll-mt-24 mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-12 gap-10">
            {/* Left: contact info */}
            <div className="col-span-12 lg:col-span-4">
              <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-6">
                / Direct lines
              </div>
              <div className="space-y-5 border-t border-ink-500 pt-6">
                <a
                  href="tel:+916362073298"
                  className="flex items-center gap-4 text-white hover:text-cy transition-colors"
                  data-testid="contact-phone"
                >
                  <PhoneCall size={20} weight="duotone" />
                  <span className="font-mono">+91 6362 073 298</span>
                </a>
                <a
                  href="mailto:connect@hihumint.com"
                  className="flex items-center gap-4 text-white hover:text-cy transition-colors"
                  data-testid="contact-email"
                >
                  <EnvelopeSimple size={20} weight="duotone" />
                  <span className="font-mono">connect@hihumint.com</span>
                </a>
                <div className="flex items-center gap-4 text-zinc-400">
                  <MapPin size={20} weight="duotone" />
                  <span className="font-mono">Bangalore · India</span>
                </div>
              </div>

              <div className="mt-12 border-t border-cy pt-3">
                <div className="font-mono text-[11px] tracking-[0.28em] text-cy uppercase mb-8">
                  What happens next
                </div>
                <ol className="space-y-8">
                  <li className="flex items-start gap-5" data-testid="next-step-01">
                    <span className="font-mono text-sm text-cy mt-0.5">01</span>
                    <span className="text-base md:text-lg text-zinc-200 leading-snug">
                      We reply within 24 hours with a suggested call slot.
                    </span>
                  </li>
                  <li className="flex items-start gap-5" data-testid="next-step-02">
                    <span className="font-mono text-sm text-cy mt-0.5">02</span>
                    <span className="text-base md:text-lg text-zinc-200 leading-snug">
                      30-min diagnostic call with a senior partner — no pitch deck.
                    </span>
                  </li>
                  <li className="flex items-start gap-5" data-testid="next-step-03">
                    <span className="font-mono text-sm text-cy mt-0.5">03</span>
                    <span className="text-base md:text-lg text-zinc-200 leading-snug">
                      You receive a preliminary diagnostic note within 5 working days.
                    </span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Right: form */}
            <div className="col-span-12 lg:col-span-8">
              <div className="border border-ink-500 bg-[#050505]">
                <div className="border-b border-ink-500 px-6 md:px-10 py-5 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                    humint.form / intake_v1
                  </span>
                  <span className="font-mono text-[11px] text-cy">
                    <span className="blink">●</span> live
                  </span>
                </div>

                {done ? (
                  <div className="p-10 md:p-16 text-center flex flex-col items-center" data-testid="contact-success">
                    <CheckCircle size={48} weight="duotone" className="text-cy" />
                    <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-white">
                      Inquiry received.
                    </h3>
                    <p className="mt-3 text-zinc-400 max-w-md">
                      A HumInt partner will be in touch personally. Meanwhile, keep building.
                    </p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-cy"
                      data-testid="contact-send-another"
                    >
                      Send another inquiry →
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={submit}
                    className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
                  >
                    <Field label="Full name" required testid="contact-input-name" value={form.name} onChange={update("name")} />
                    <Field label="Work email" type="email" required testid="contact-input-email" value={form.email} onChange={update("email")} />
                    <Field label="Company" testid="contact-input-company" value={form.company} onChange={update("company")} />
                    <Field label="Phone" testid="contact-input-phone" value={form.phone} onChange={update("phone")} />

                    <div className="md:col-span-2">
                      <Label>Your primary challenge</Label>
                      <select
                        value={form.service_interest}
                        onChange={update("service_interest")}
                        data-testid="contact-select-challenge"
                        className="w-full bg-transparent border border-ink-500 focus:border-cy focus:outline-none px-4 py-3 text-sm text-white"
                      >
                        <option value="" className="bg-ink">Select a challenge…</option>
                        {challenges.map((c) => (
                          <option key={c.id} value={c.label} className="bg-ink">
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <Label required>Tell us more</Label>
                      <textarea
                        required
                        rows={5}
                        data-testid="contact-input-challenge"
                        value={form.challenge}
                        onChange={update("challenge")}
                        placeholder="What's the context? What have you tried? What outcome would make this engagement a success?"
                        className="w-full bg-transparent border border-ink-500 focus:border-cy focus:outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-600"
                      />
                    </div>

                    <div className="md:col-span-2 flex items-center justify-between pt-2">
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                        Reviewed personally · Bangalore time
                      </span>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="group inline-flex items-center gap-3 bg-cy text-ink px-7 py-3 text-sm font-semibold hover:bg-white disabled:opacity-60 transition-colors"
                      >
                        {submitting ? "Sending…" : "Send inquiry"}
                        <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Label({ children, required }) {
  return (
    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
      {children}
      {required && <span className="text-cy ml-1">*</span>}
    </label>
  );
}

function Field({ label, required, type = "text", testid, value, onChange }) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border border-ink-500 focus:border-cy focus:outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-600"
      />
    </div>
  );
}
